/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { useEffect } from 'react';
import {
  Box,
  Divider,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  styled,
  useTheme,
} from '@mui/material';
import Typography from '../../../../../components/Typography';
import { ICampaignState, useCampaignContext } from '../CampaignContext';
import { AccordionItemSummary, getTotalUnits, SalesAccordionItem } from '../QuantityTab';
import {
  selectCurrentFarmerAccount,
  selectEmailForPrefill,
  selectNameForPrefill,
  selectPhoneForPrefill,
} from '../../../../azure-b2c/storage';
import { useSelector } from 'react-redux';
import {
  BoldWrapper,
  OrderLocationButton,
  PadButton,
  PadContent,
  PadIcon,
  WrapperA,
} from '../../../../../components/Pad/Pad';
import Input, { InputProps } from '../../../../../components/Input';
import {
  FormikAgreementForm,
  SaleAgreementPromo,
  SalesAgreementPromoProductSelectionVariant,
} from '../types';
import Checkbox from '../../../../../components/Checkbox';
import { Form, Formik, useFormikContext } from 'formik';
import { nzPhoneNumberRegex } from '../../../../../util/validatePhoneNumber';
import TextField from '../../../../../components/TextField';
import MaterialSymbol from '../../../../../components/MaterialSymbol';
import _ from 'lodash';
import { splitAddress } from '../../../../../services/als/splitAddress';
import { addGST, getGST } from '../../../../../util/addGst';
import { formatPrice } from '../../../../../util/helpers';
import { OrderSummary } from '../../../../../components/OrderSummary';
import TestIds from '../../../../../testIds';
import { submitCampaignApi } from '../api';
import { Loader } from '../../../../../components/Loader';
import { useNavigate } from 'react-router-dom';
import { colors } from '@farmlands/mui-theme';
import { calculateTotalFreightCost, getPrice } from '../utils';
import TabHeader from '../TabHeader';
import { nameRegex } from '../../../../../util/validateName';
import { z } from 'zod';
import { withZodSchema } from 'formik-validator-zod';
import { ErrorCode } from '../../../../drawdown/v2/DrawdownRequestModal/api';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { ReviewTabFooter } from './ReviewTabFooter';
import { RESTART_ERROR } from '../constants';

const calculateTotalPrice = (products: SalesAgreementPromoProductSelectionVariant[]) => {
  return products.reduce((total, product) => {
    if (!product.orders) return 0;
    const totalOrders = product.orders.reduce((sum, val) => sum + val.quantity, 0);
    return total + totalOrders * getPrice(product);
  }, 0);
};

const StyledSplitPaymentInnerContainer = styled(Stack)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
  },
}));

const StyledSplitPaymentContainer = styled(Stack, {
  shouldForwardProp: (prop) => prop !== '',
})<{ checked?: boolean }>(({ theme, checked }) => ({
  background: checked ? theme.colors.primary100 : 'transparent',
  borderRadius: checked ? '8px' : '0',
  gap: '4px',
  padding: '8px',
}));

const StyledSummaryContainer = styled(Stack)(({ theme }) => ({
  background: theme.colors.primary50,
  border: `1px solid ${theme.colors.primary200}`,
}));

function hasAnyNonNullishField<T extends Record<string, unknown>>(obj: T): boolean {
  return Object.values(obj).some((value) => value != null);
}

export interface ReviewTabProps {
  config: SaleAgreementPromo;
  refreshCampaign: () => void;
}

export const ReviewTab: React.FC<ReviewTabProps> = ({ config, refreshCampaign }) => {
  const account = useSelector(selectCurrentFarmerAccount);
  const { state, setState, reset } = useCampaignContext();
  const isFreightInclusive = !config.freightRequired;
  const [submitting, setSubmitting] = React.useState(false);
  const [apiError, setApiError] = React.useState<string>();
  const navigate = useNavigate();

  const totalFreightCost = isFreightInclusive
    ? 0
    : calculateTotalFreightCost(state.productSelected, state.freightCharge);
  const totalPrice = calculateTotalPrice(state.productSelected);

  const accountEmail = useSelector(selectEmailForPrefill);
  const accountUsername = useSelector(selectNameForPrefill);
  const accountPhone = useSelector(selectPhoneForPrefill);

  const dairySupplyNum =
    state.addressSelected && 'dairySupplyNumber' in state.addressSelected
      ? (state.addressSelected.dairySupplyNumber as string)
      : '';

  const initialValues: FormikAgreementForm = React.useMemo(() => {
    return {
      accName: accountUsername ?? '',
      accEmail: accountEmail ?? '',
      accPhoneNumber: accountPhone ?? '',
      productsSelected: state.productSelected,
      splitPayment: false,
      reference: '',
      splitPaymentAccName: '',
      splitPaymentAccNumber: '',
      splitPaymentContribution: '0',
      tcAccepted: false,
      deliveryContactName: '',
      deliveryPhoneNum: '',
      deliverySupplyNum: dairySupplyNum,
      deliveryDetails: '',
      freightType: '',
      hiabTruckRequired: false,
      truck: '',
      carrier: '',
    };
  }, [accountUsername, accountEmail, accountPhone, state.productSelected, dairySupplyNum]);

  const hasFreightOptions = !!(
    state.deliveryOption &&
    state.deliveryOption.freightOptions &&
    state.deliveryOption.freightOptions.length >= 1
  );

  const hasTruckOptions = !!(
    state.deliveryOption?.trucks && state.deliveryOption.trucks.length >= 1
  );
  const isDeliveryMethod = state.deliveryOption?.type === 'Delivery';

  const carrierOptions = (value: FormikAgreementForm['freightType']) =>
    state.deliveryOption?.freightOptions?.find((a) => a.type === value)?.carriers ?? [];

  const hasCarrierOptions = (value: FormikAgreementForm['freightType']) => {
    if (!value) return false;
    if (!carrierOptions(value)) return false;

    return carrierOptions(value)?.length && carrierOptions(value)?.length >= 1;
  };

  const freightSummary = React.useMemo(() => {
    if (isFreightInclusive) {
      return {
        deliveryCost: '',
        DeliveryLabel: <></>,
        FreightNoted: (
          <Typography variant='body2' fontWeight={600} textAlign='right'>
            {config.freightNote}
          </Typography>
        ),
      };
    }

    return {
      deliveryCost: formatPrice(addGST(totalFreightCost)),
      DeliveryLabel: (
        <Typography variant='body2'>
          Freight&nbsp;
          <Typography fontSize='12px' component='span' lineHeight={'18px'}>
            (GST incl.)
          </Typography>
        </Typography>
      ),
      FreightNoted: null,
    };
  }, [isFreightInclusive, config]);

  const checkTnC = config.termsConditionsTickRequired ? z.literal(true) : z.boolean().optional();
  const [overtime, setOvertime] = React.useState(false);

  useEffect(() => {
    // Check initially
    setOvertime(dayjs().diff(state.timer, 'minutes') >= 60);

    // Set up interval to check every second
    const interval = setInterval(() => {
      setOvertime(dayjs().diff(state.timer, 'minutes') >= 60);
    }, 1000);

    // Clean up interval on unmount
    return () => clearInterval(interval);
  }, [state.timer]);

  const zodValidationSchema = (values: FormikAgreementForm) =>
    z
      .object({
        accName: z.string().min(1, 'Please enter a name.'),
        accEmail: z.string().email('Invalid email format.').min(1, 'Please enter an email.'),
        accPhoneNumber: z
          .string()
          .trim()
          .regex(nzPhoneNumberRegex, 'Invalid phone number format.')
          .min(1, 'Please enter a phone number.'),
        reference: z.string().max(60, 'Maximum 60 characters allowed.').optional(),
        splitPayment: z.boolean(),
        splitPaymentAccName: z.string().optional(),
        splitPaymentAccNumber: z.string().optional(),
        splitPaymentContribution: z.string().optional(),
        tcAccepted: !account?.hasTfoAccess ? z.literal(true) : checkTnC,
        deliveryContactName: isDeliveryMethod
          ? z
              .string()
              .regex(nameRegex, 'Invalid name format. (alphabets only)')
              .min(1, 'Delivery contact name is required.')
          : z.string().optional(),
        deliveryPhoneNum: isDeliveryMethod
          ? z
              .string()
              .trim()
              .regex(nzPhoneNumberRegex, 'Invalid phone number format.')
              .min(1, 'Delivery phone number is required.')
          : z.string().optional(),
        deliverySupplyNum: z.string().optional(),
        deliveryDetails: z.string().optional(),
        hiabTruckRequired: z.boolean().optional(),
        freightType: hasFreightOptions
          ? z.string().min(1, 'Please choose an option.')
          : z.string().optional(),
        truck: hasTruckOptions
          ? z.string().min(1, 'Please choose a truck.')
          : z.string().optional(),
        carrier: hasCarrierOptions(values.freightType)
          ? z.string().min(1, 'Please choose a carrier.')
          : z.string().optional(),
      })
      .superRefine((data, ctx) => {
        if (data.splitPayment) {
          if (!data.splitPaymentAccName) {
            ctx.addIssue({
              path: ['splitPaymentAccName'],
              message: 'Required.',
              code: z.ZodIssueCode.custom,
            });
          }
          if (!data.splitPaymentAccNumber) {
            ctx.addIssue({
              path: ['splitPaymentAccNumber'],
              message: 'Required.',
              code: z.ZodIssueCode.custom,
            });
          } else if (!/^\d*$/.test(data.splitPaymentAccNumber)) {
            ctx.addIssue({
              path: ['splitPaymentAccNumber'],
              message: 'Number format only.',
              code: z.ZodIssueCode.custom,
            });
          } else if (data.splitPaymentAccNumber.length !== 9) {
            ctx.addIssue({
              path: ['splitPaymentAccNumber'],
              message: 'Must be 9 digit number.',
              code: z.ZodIssueCode.custom,
            });
          }

          if (
            data.splitPaymentContribution === undefined ||
            Number(data.splitPaymentContribution) < 1
          ) {
            ctx.addIssue({
              path: ['splitPaymentContribution'],
              message: 'Minimum 1% or more.',
              code: z.ZodIssueCode.custom,
            });
          }
        }
      });

  const resetForm = () => {
    reset();
    refreshCampaign();
    setState({ timer: Date.now(), tabIndex: 0 });
  };

  return (
    <Formik<FormikAgreementForm>
      initialValues={initialValues}
      validate={(values) => withZodSchema(zodValidationSchema(values))(values)}
      onSubmit={(value) => {
        if (account) {
          setSubmitting(true);
          submitCampaignApi(value, state, account, config)
            .then(() => {
              navigate('/agreements/campaign-success', {
                state: {
                  campaignTitle: config?.displayTitle ?? config?.documentTitle,
                },
              });
            })
            .catch((error: AxiosError<{ type: ErrorCode }>) => {
              if (
                error.response?.data.type &&
                [
                  ErrorCode.InvalidAgreement,
                  ErrorCode.InvalidAgreementLine,
                  ErrorCode.InvalidAgreementQuantity,
                  ErrorCode.InvalidAgreementSchedule,
                ].includes(error.response?.data.type)
              ) {
                setApiError(RESTART_ERROR);
                refreshCampaign();
              } else {
                setApiError(
                  'We’re sorry, but an unexpected error has occurred. We are unable to submit this agreement at this time.',
                );
              }
              console.log(error);
            })
            .finally(() => {
              setSubmitting(false);
            });
        }
      }}
    >
      {({ setFieldValue, errors, submitForm }) => (
        <Form key={'campaign-submission'} style={{ height: '100%' }}>
          <Stack height='100%' margin='auto' display={submitting ? 'flex' : 'none'}>
            <Loader title='Submitting' />
          </Stack>
          <Stack
            direction='column'
            gap={'1rem'}
            pb='50px'
            display={submitting ? 'none' : undefined}
          >
            <TabHeader title='Summary' caption='Review your products and confirm agreement' />
            <ProductsSelected isFreightInclusive={isFreightInclusive} />
            <StyledSummaryContainer p='1rem' direction='column'>
              <Stack direction={'column'} gap={'1rem'}>
                <OrderSummary
                  showDivider
                  accountName={account?.accountName}
                  accountNumber={account?.accountNumber}
                  gst={formatPrice(getGST(totalPrice + totalFreightCost))}
                  title='Order Summary'
                  total={formatPrice(addGST(totalPrice + totalFreightCost))}
                  delivery={freightSummary.deliveryCost}
                  deliveryLabel={freightSummary.DeliveryLabel}
                />

                {isDeliveryMethod ? (
                  <>
                    {freightSummary.FreightNoted}
                    <DeliveryAddressSelected />
                    <DeliveryInfoForm state={state} />
                  </>
                ) : null}

                <AccountHolder />

                <Stack>
                  <SplitPaymentOption />
                  {!account?.hasTfoAccess ? (
                    <StyledSplitPaymentContainer>
                      <Checkbox
                        label={
                          <Typography variant='body2' fontWeight={400}>
                            I accepted the&nbsp;
                            <Link target='_blank' href={config.termsConditionsUrl}>
                              terms and conditions
                            </Link>
                            &nbsp;for these products
                          </Typography>
                        }
                        onChange={(_, checked) => setFieldValue('tcAccepted', checked)}
                      />
                      {errors.tcAccepted && (
                        <Typography variant='body2' color='error500'>
                          Terms and Conditions must be accepted
                        </Typography>
                      )}
                    </StyledSplitPaymentContainer>
                  ) : config.termsConditionsTickRequired ? (
                    <StyledSplitPaymentContainer>
                      <Checkbox
                        label={
                          <Typography variant='body2' fontWeight={400}>
                            {config.termsConditionsText}
                          </Typography>
                        }
                        onChange={(_, checked) => setFieldValue('tcAccepted', checked)}
                      />
                      {errors.tcAccepted && (
                        <Typography variant='body2' color='error500'>
                          Terms and Conditions must be accepted
                        </Typography>
                      )}
                    </StyledSplitPaymentContainer>
                  ) : (
                    <Typography variant='body2' color='grey800'>
                      {config.termsConditionsText}
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </StyledSummaryContainer>
          </Stack>
          <ReviewTabFooter
            submitting={submitting}
            submitForm={submitForm}
            resetForm={resetForm}
            hasNonNullishField={hasAnyNonNullishField(errors)}
            errorMessage={apiError}
            overtime={overtime}
          />
        </Form>
      )}
    </Formik>
  );
};

interface FormikInputProps
  extends Pick<
    InputProps,
    | 'label'
    | 'testId'
    | 'type'
    | 'multiline'
    | 'placeholder'
    | 'required'
    | 'helperText'
    | 'helperTextColor'
    | 'disableError'
    | 'disabled'
    | 'inputProps'
    | 'minRows'
  > {
  formKey: keyof FormikAgreementForm;
}

const FormikInput: React.FC<FormikInputProps> = ({ formKey, ...restProps }) => {
  const ctx = useFormikContext<FormikAgreementForm>();

  const theme = useTheme();
  if (typeof ctx.values[formKey] !== 'string') return;
  const error = ctx.errors[formKey] as string | undefined;

  return (
    <Input
      onChange={(e) => {
        ctx.setFieldValue(formKey, e.target.value);
      }}
      value={ctx.values[formKey]}
      error={Boolean(error)}
      errorMessage={error}
      helperTextColor={theme.colors.grey700}
      {...restProps}
    />
  );
};

const FormikSelect: React.FC<
  Omit<FormikInputProps, 'type'> & { options: { label: string; value: string }[] }
> = ({ options, formKey, testId, label, required, placeholder }) => {
  const ctx = useFormikContext<FormikAgreementForm>();
  const theme = useTheme();
  const error = ctx.errors[formKey] as string | undefined;

  return (
    <Box flexGrow={1} width={'100%'} display='flex' flexDirection='column'>
      <Box display='flex' gap={1}>
        <InputLabel htmlFor={testId}>{label}</InputLabel>
        <InputLabel
          sx={{
            fontWeight: '400',
            color: !error ? colors.grey700 : colors.error700,
          }}
        >
          {required ? '(Required)' : ''}
        </InputLabel>
      </Box>
      <Select
        data-id={testId}
        value={ctx.values[formKey]}
        onChange={(e) => ctx.setFieldValue(formKey, e.target.value, true)}
        sx={{
          '.MuiSelect-icon': {
            top: 'calc(50% - .5em) !important',
          },
        }}
      >
        <MenuItem value='' disabled>
          {placeholder}
        </MenuItem>
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
      {ctx.errors[formKey] ? (
        <FormHelperText error sx={{ color: theme.colors.error700 }}>
          {String(ctx.errors[formKey])}
        </FormHelperText>
      ) : null}
    </Box>
  );
};

const FormikRadio: React.FC<
  Omit<FormikInputProps, 'label' | 'type'> & { options: { label: string; value: string }[] }
> = ({ options, formKey, testId }) => {
  const ctx = useFormikContext<FormikAgreementForm>();
  const theme = useTheme();
  return (
    <Stack>
      <RadioGroup
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}
        data-id={testId}
        value={ctx.values[formKey]}
        onChange={(e) => ctx.setFieldValue(formKey, e.target.value, true)}
      >
        {options.map((opt) => (
          <FormControlLabel
            slotProps={{
              typography: {
                variant: 'body2',
              },
            }}
            key={opt.value}
            label={opt.label}
            value={opt.value}
            control={<Radio />}
          />
        ))}
      </RadioGroup>
      {ctx.errors[formKey] ? (
        <FormHelperText error sx={{ color: theme.colors.error700 }}>
          {String(ctx.errors[formKey])}
        </FormHelperText>
      ) : null}
    </Stack>
  );
};

const FormikInputPercent: React.FC<FormikInputProps> = ({ formKey, type, testId, label }) => {
  const ctx = useFormikContext<FormikAgreementForm>();
  const theme = useTheme();
  const error = ctx.errors[formKey] as string | undefined;

  return (
    <TextField
      sx={{ padding: '10.5px 8px' }}
      label={label}
      type={type}
      onChange={(e) => {
        const val = e.target.value;

        if (RegExp(/\D/).exec(val)) {
          return e.preventDefault();
        }

        if (Number(val) > 100) return;

        ctx.setFieldValue(formKey, _.trimStart(String(val), '0'));
      }}
      value={String(ctx.values[formKey])}
      variant={'outlined'}
      testId={testId}
      inputProps={{
        inputMode: 'numeric',
      }}
      error={Boolean(error)}
      helperText={typeof ctx.errors[formKey] === 'string' ? ctx.errors[formKey] : ' '}
      helperTextColor={theme.colors.error700}
      InputProps={{
        endAdornment: <MaterialSymbol color={theme.colors.primary500}>percent</MaterialSymbol>,
      }}
    />
  );
};

interface ProductsSelectedProps {
  isFreightInclusive: boolean;
}

const ProductsSelected = ({ isFreightInclusive }: ProductsSelectedProps) => {
  const { state } = useCampaignContext();

  return state.productSelected.map((prod) => {
    const summary: AccordionItemSummary[] = [
      {
        type: 'unit',
        title: prod.unit,
        calculation: prod.orders ? prod.orders.reduce((a, b) => a + b.quantity, 0) : 0,
      },
      {
        type: 'price',
        title: 'Total',
        calculation: getPrice(prod) * getTotalUnits(state.productSelected, prod.productId),
      },
    ];

    if (!isFreightInclusive) {
      summary.push({
        type: 'price',
        title: 'Freight',
        calculation: calculateTotalFreightCost(prod, state.freightCharge),
      });
    }

    return (
      <SalesAccordionItem
        key={prod.productId}
        productId={prod.productId}
        unit={prod.unit}
        itemNumber={prod.itemNumber}
        minQuantity={prod.quantity}
        title={prod.description}
        deliveryMonthPlans={prod.deliveryMonthPlans}
        selectedDeliveryMonthPlan={prod.selectedDeliveryMonthPlan}
        editable={false}
        defaultExpanded={true}
        summary={summary}
      />
    );
  });
};

const DeliveryAddressSelected = () => {
  const { state, setState } = useCampaignContext();
  const handleOpenPickUpStoreModal = () => {
    setState({ tabIndex: 0 });
  };

  if (state.addressSelected) {
    const address = state.addressSelected;
    let subtitle = '';
    if ('UniqueId' in address) {
      const { first, second } = splitAddress(address.FullAddress);
      subtitle = `${first}, ${second}`;
    } else {
      subtitle = `${address.street.replace('\n', ', ')} ${address.city}, ${address.zipCode}`;
    }
    return (
      <OrderLocationButton
        shade='light'
        onClick={handleOpenPickUpStoreModal}
        buttonTestId={TestIds.feat.campaign.reviewTab.orderLocationButton}
        title='Delivery'
        subtitle={
          <>
            {subtitle.split('\n').map((e) => {
              return (
                <span key={e}>
                  {e}
                  <br />
                </span>
              );
            })}
          </>
        }
        isEditEnabled
      />
    );
  }
  return (
    <PadButton
      shade='light'
      sx={{ justifyContent: 'flex-start', p: 1 }}
      onClick={handleOpenPickUpStoreModal}
      data-testid={TestIds.feat.campaign.reviewTab.pickupOrDelivery}
    >
      <WrapperA>
        <BoldWrapper>
          <PadIcon className='material-symbols-outlined'>add</PadIcon>
          <PadContent>Select pickup store or delivery</PadContent>
        </BoldWrapper>
      </WrapperA>
    </PadButton>
  );
};

const DeliveryInfoForm = ({ state }: { state: ICampaignState }) => {
  const { values, setFieldValue } = useFormikContext<FormikAgreementForm>();

  const carrierOptions = state.deliveryOption?.freightOptions
    ?.find((a) => a.type === values.freightType)
    ?.carriers?.map((c) => ({
      label: c,
      value: c,
    }));

  useEffect(() => {
    setFieldValue('carrier', '', false);
  }, [values.freightType]);

  return (
    <Stack gap={1}>
      <FormikInput
        label='Delivery contact name'
        formKey='deliveryContactName'
        placeholder='Enter a delivery contact name here...'
        testId={TestIds.feat.campaign.reviewTab.deliveryInfo.contactNameInput}
        required
        type='text'
      />

      <FormikInput
        label='Delivery phone number'
        formKey='deliveryPhoneNum'
        placeholder='Enter a delivery phone number...'
        testId={TestIds.feat.campaign.reviewTab.deliveryInfo.phoneNumberInput}
        required
        type='tel'
      />

      <FormikInput
        type='tel'
        label='Dairy supply number'
        formKey='deliverySupplyNum'
        placeholder='Include your Dairy supply number, if you have one.'
        inputProps={{ maxLength: 60 }}
        testId={TestIds.feat.campaign.reviewTab.deliveryInfo.supplyNumberInput}
        // helperText=''
        // helperTextColor={colors.grey700}
      />

      <FormikInput
        type='text'
        multiline
        minRows={4}
        placeholder='Enter additional delivery details here...'
        formKey='deliveryDetails'
        label='Delivery Details'
        testId={TestIds.feat.campaign.reviewTab.deliveryInfo.detailsInput}
      />
      {(state.deliveryOption &&
        state.deliveryOption.freightOptions &&
        state.deliveryOption.freightOptions.length >= 1) ||
      (state.deliveryOption?.trucks && state.deliveryOption.trucks.length >= 1) ||
      carrierOptions ? (
        <Divider sx={{ mb: '24px' }} />
      ) : null}
      {state.deliveryOption &&
      state.deliveryOption.freightOptions &&
      state.deliveryOption.freightOptions.length >= 1 ? (
        <FormikRadio
          options={state.deliveryOption.freightOptions.map((fo) => ({
            label: fo.text,
            value: fo.type,
          }))}
          formKey='freightType'
          testId={TestIds.feat.campaign.reviewTab.deliveryInfo.radioGroup}
        />
      ) : null}

      {(state.deliveryOption?.trucks && state.deliveryOption.trucks.length >= 1) ||
      (carrierOptions && carrierOptions.length >= 1) ? (
        <Typography variant='body2' my={2}>
          Please select your preferred carrier, if any
        </Typography>
      ) : null}
      {state.deliveryOption?.trucks && state.deliveryOption.trucks.length >= 1 ? (
        <FormikSelect
          label='Truck type'
          required
          options={state.deliveryOption.trucks.map((val) => ({
            label: val,
            value: val,
          }))}
          testId={TestIds.feat.campaign.reviewTab.deliveryInfo.selectTruck}
          formKey='truck'
          placeholder='Please choose a truck...'
        />
      ) : null}
      {carrierOptions && carrierOptions.length >= 1 ? (
        <FormikSelect
          label='Carrier'
          options={carrierOptions}
          testId={TestIds.feat.campaign.reviewTab.deliveryInfo.selectCarrier}
          formKey='carrier'
          required
          placeholder='Please choose a carrier...'
        />
      ) : null}
    </Stack>
  );
};

const AccountHolder = () => {
  const account = useSelector(selectCurrentFarmerAccount);
  const accountEmail = useSelector(selectEmailForPrefill);
  const accountUsername = useSelector(selectNameForPrefill);
  const { values } = useFormikContext<FormikAgreementForm>();

  const confirmTitle = 'Account holder / authoriser';
  const nameLabel = account?.hasTfoAccess ? 'Customer name' : 'Name';
  const phoneLabel = account?.hasTfoAccess ? 'Customer phone number' : 'Phone number';
  const emailLabel = account?.hasTfoAccess ? 'Customer email address' : 'Email address';
  const refLabel = account?.hasTfoAccess ? 'Customer reference' : 'Your reference';

  return (
    <Stack direction={'column'} py='0.5rem' gap={2}>
      <Typography variant='h6' fontWeight={700} fontSize={'20px'}>
        {confirmTitle}
      </Typography>
      <Typography variant='body2'>Please confirm your contact details</Typography>

      {account?.hasTfoAccess && (
        <Stack width='auto' borderLeft={`4px solid ${colors.grey900}`} pl={'8px'} gap={'2px'}>
          <Typography variant='body2' fontWeight={'bold'}>
            {account?.accountName}
          </Typography>
          <Typography variant='body2'>{account.accountNumber}</Typography>
        </Stack>
      )}
      <Stack>
        {!account?.hasTfoAccess && accountUsername !== undefined ? (
          <Stack gap={0.2} mb={2} borderLeft={`4px solid ${colors.grey900}`} pl={2}>
            <Typography variant='body2' fontWeight={'bold'}>
              {account?.accountName}
            </Typography>
            <Typography variant='body2'>{account?.accountNumber}</Typography>
            <Typography variant='body2'>{accountUsername}</Typography>
            <Typography variant='body2'>{accountEmail}</Typography>
          </Stack>
        ) : (
          <>
            <FormikInput
              type='text'
              label={nameLabel}
              testId={TestIds.feat.campaign.reviewTab.nameInput}
              required
              formKey='accName'
            />
            <FormikInput
              type='text'
              label={emailLabel}
              testId={TestIds.feat.campaign.reviewTab.emailInput}
              required
              formKey='accEmail'
            />
          </>
        )}

        <FormikInput
          type='tel'
          label={phoneLabel}
          testId={TestIds.feat.campaign.reviewTab.phoneInput}
          required
          formKey='accPhoneNumber'
        />
        <FormikInput
          type='text'
          label={refLabel}
          placeholder='e.g. Your Purchase Order reference if required'
          testId={TestIds.feat.campaign.reviewTab.refInput}
          formKey='reference'
          helperText={`${values.reference.length}/60 characters`}
          inputProps={{ maxLength: 60 }}
          disableError
        />
      </Stack>
    </Stack>
  );
};

const SplitPaymentOption = () => {
  const { values, setFieldValue } = useFormikContext<FormikAgreementForm>();
  return (
    <StyledSplitPaymentContainer checked={values.splitPayment}>
      <Checkbox
        label={
          <Typography variant='body2' fontWeight={400}>
            Split payment with another farm
          </Typography>
        }
        onChange={(_, checked) => setFieldValue('splitPayment', checked)}
      />
      {values.splitPayment && (
        <StyledSplitPaymentInnerContainer>
          <FormikInput
            type='text'
            formKey='splitPaymentAccName'
            label='Account name'
            placeholder=''
            testId={TestIds.feat.campaign.reviewTab.splitPay.accNameInput}
          />
          <FormikInput
            type='text'
            formKey='splitPaymentAccNumber'
            label='Account number'
            placeholder=''
            testId={TestIds.feat.campaign.reviewTab.splitPay.accNumInput}
            inputProps={{ maxLength: 9 }}
          />
          <Stack gap='24px' direction={'row'} alignItems={'flex-start'}>
            <Stack width='30%' minWidth='140px'>
              <FormikInputPercent
                formKey='splitPaymentContribution'
                label='Their contribution'
                placeholder=''
                testId={TestIds.feat.campaign.reviewTab.splitPay.splitInput}
                type='number'
              />
            </Stack>
            <Stack alignSelf='center'>
              <Typography>-</Typography>
            </Stack>
            <Stack>
              <InputLabel>My contribution</InputLabel>
              <Stack direction='row' height='48px'>
                <Typography
                  alignSelf={'center'}
                  data-testid={TestIds.feat.campaign.reviewTab.splitPay.splitResult}
                >
                  {`${100 - Number(values.splitPaymentContribution)}%`}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </StyledSplitPaymentInnerContainer>
      )}
    </StyledSplitPaymentContainer>
  );
};
